import React, { Fragment, useEffect, useState, useCallback } from "react";
import useUrlParams from "../../../../hooks/useUrlParams";
import useTrueTwin from "../../../../hooks/useTrueTwin";
import CommandCenterService from "../../../../../../service/CommandCenterService";
import { showToastMessage } from "../../../../../../utils/commonFunctions";
import { VALID_JSON_EVENT } from "../../../../../../utils/constants";
import DualEditorForm from "./DualEditorForm";
import { TestCaseDetailsShimmer } from "./components/Test/Test";
import "./Console.scss";

interface CustomTcFormProps {}

const CustomTcForm: React.FC<CustomTcFormProps> = () => {
  const { truetwin_id } = useTrueTwin();
  const { dashboard, scenario_id } = useUrlParams();

  const [loading, setLoading] = useState<boolean>(false);
  const [customTcData, setCustomTcData] = useState<any | null>(null);
  const [selectedTelecommand, setSelectedTelecommand] = useState<any | null>(null);
  const [previewTelecommandData, setPreviewTelecommandData] = useState<any>({});

  const setCustomTcFormData = useCallback(
    (data: any) => {
      const formData: Array<{
        TruetwinId?: string;
        LinkId: string;
        scenarioId?: string;
        Timestamp: null | number;
        Type: string;
        ScheduleId: string;
        TaskId: string;
        sequenceData: any;
        CommandName?: string;
        isCustomTc?: boolean;
      }> = [
        {
          TruetwinId: truetwin_id,
          LinkId: "",
          scenarioId: scenario_id,
          Timestamp: null,
          Type: "completeJson",
          ScheduleId: "default",
          TaskId: "default",
          sequenceData: data?.sequenceData,
          isCustomTc: true,
        },
      ];

      if (dashboard === "operate") {
        formData[0].CommandName = data?.commandName;
      }

      sessionStorage.setItem("formData", JSON.stringify(formData));
      return formData;
    },
    [truetwin_id, scenario_id, dashboard]
  );

  const fetchCustomTc = useCallback(async () => {
    if (!truetwin_id) return;
    setLoading(true);
    try {
      const res: any = await CommandCenterService.getObject(truetwin_id, true);
      if (res?.data) {
        const item = Array.isArray(res.data) ? res.data[0] : res.data;
        if (item) {
          setCustomTcData(item);
          const teledata = {
            sequenceData: item?.sequenceData || {},
            commandName: item?.testCaseName || item?.name || "CustomTc",
            commandType: "BUS",
          };
          const tcOption = { label: item?.testCaseName || "Custom Tc", value: item };
          setSelectedTelecommand(tcOption);
          setPreviewTelecommandData(teledata);
          setCustomTcFormData(teledata);

          sessionStorage.setItem("selectedTelecommand", JSON.stringify(tcOption));
          sessionStorage.setItem("previewTelecommandData", JSON.stringify(teledata));
          window.dispatchEvent(new Event("storageUpdate"));
          window.dispatchEvent(new CustomEvent(VALID_JSON_EVENT, { detail: { buttonOn: true } }));
        }
      }
    } catch (err) {
      console.error("Error fetching Custom TC:", err);
      showToastMessage("error", "Failed to fetch Custom TC template");
    } finally {
      setLoading(false);
    }
  }, [truetwin_id, setCustomTcFormData]);

  useEffect(() => {
    sessionStorage.setItem("currentForm", "customTc");
    window.dispatchEvent(new Event("storageUpdate"));
    fetchCustomTc();
  }, [fetchCustomTc]);

  useEffect(() => {
    sessionStorage.setItem("previewTelecommandData", JSON.stringify(previewTelecommandData));
    sessionStorage.setItem("selectedTelecommand", JSON.stringify(selectedTelecommand));
    window.dispatchEvent(new Event("storageUpdate"));
  }, [previewTelecommandData, selectedTelecommand]);

  useEffect(() => {
    const clearFormFields = () => {
      if (customTcData) {
        const teledata = {
          sequenceData: customTcData?.sequenceData || {},
          commandName: customTcData?.testCaseName || customTcData?.name || "CustomTc",
          commandType: "BUS",
        };
        setCustomTcFormData(teledata);
      }
      sessionStorage.setItem("currentForm", "customTc");
      window.dispatchEvent(new Event("storageUpdate"));
    };

    window.addEventListener("clearFormFields", clearFormFields);
    return () => {
      window.removeEventListener("clearFormFields", clearFormFields);
    };
  }, [customTcData, setCustomTcFormData]);

  return (
    <Fragment>
      {loading ? (
        <TestCaseDetailsShimmer />
      ) : (
        <DualEditorForm
          jsonData={previewTelecommandData}
          setPreviewTeleCommandData={setPreviewTelecommandData}
          setBusFormData={setCustomTcFormData}
          selectedTelecommand={selectedTelecommand}
          isCustomTc={true}
          mode="customTc"
        />
      )}
    </Fragment>
  );
};

export default CustomTcForm;
